import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.smartqrmnavigator.com';

const SEO_DATA = {
  '/': {
    title: 'Smart QRM Navigator | Quick Responsive Manufacturing Tools by Jan Pauwels',
    description: 'AI-powered Quick Responsive Manufacturing (QRM) strategy platform by Jan Pauwels, JP Consulting. EBITDA calculator, strategy advisor, and implementation tools inspired by Rajan Suri\'s QRM methodology.',
    og_title: 'Smart QRM Navigator — Stop Guessing, Start Calculating',
  },
  '/success-cases': {
    title: 'QRM Success Cases | Real Manufacturing Results — Smart QRM Navigator',
    description: 'Discover real Quick Responsive Manufacturing success stories including PMC Pattyn and other manufacturers. See how QRM reduced lead times by 50-80% and improved EBITDA margins.',
    og_title: 'QRM Success Cases — Proven Manufacturing Transformations',
  },
  '/qrm-vocabulary': {
    title: 'QRM & Lean Vocabulary | Quick Responsive Manufacturing Glossary — Jan Pauwels',
    description: 'Complete QRM and Lean manufacturing vocabulary. Key terms from Rajan Suri\'s Quick Responsive Manufacturing methodology explained by Jan Pauwels, JP Consulting.',
    og_title: 'QRM & Lean Vocabulary — Essential Manufacturing Terms',
  },
  '/qrm-journey': {
    title: 'Jan Pauwels QRM Journey | Mentors Pascal Pollet & Rajan Suri — JP Consulting',
    description: 'Jan Pauwels shares his QRM journey with mentors Pascal Pollet and Rajan Suri. From PMC Pattyn transformation to JP Consulting — 10 years of Quick Responsive Manufacturing expertise.',
    og_title: 'Jan Pauwels — My QRM Journey & Mentors',
  },
  '/qrm-education': {
    title: 'QRM Education & Training | Quick Responsive Manufacturing Courses — Jan Pauwels',
    description: 'Learn Quick Responsive Manufacturing (QRM) with structured training from Jan Pauwels, JP Consulting. Courses covering Rajan Suri\'s QRM principles, POLCA, and lead time reduction.',
    og_title: 'QRM Education & Training — Learn Quick Responsive Manufacturing',
  },
  '/business-case': {
    title: 'EBITDA Business Case Calculator | QRM ROI Tool — Smart QRM Navigator',
    description: 'Calculate your QRM investment ROI with our EBITDA Business Case Calculator. 3-year projections, payback period analysis, and financial impact of Quick Responsive Manufacturing implementation.',
    og_title: 'EBITDA Business Case Calculator — QRM Investment ROI',
  },
  '/recommendations': {
    title: 'QRM Strategy Advisor | AI-Powered Manufacturing Recommendations — Jan Pauwels',
    description: 'Get AI-powered QRM strategy recommendations tailored to your manufacturing situation. Compare Quick Responsive Manufacturing, Lean, and TOC approaches with Jan Pauwels\' Strategy Advisor.',
    og_title: 'QRM Strategy Advisor — AI-Powered Recommendations',
  },
  '/strategic-consulting': {
    title: 'Strategic QRM Consulting | Jan Pauwels, JP Consulting',
    description: 'Strategic Quick Responsive Manufacturing consulting by Jan Pauwels, JP Consulting. Expert guidance on QRM implementation, lead time reduction, and manufacturing transformation.',
    og_title: 'Strategic QRM Consulting — JP Consulting',
  },
  '/bani-tuna': {
    title: 'BANI/TUNA Framework for QRM | Manufacturing Chaos to Quick Responsive — Jan Pauwels',
    description: 'Understand the BANI/TUNA framework and how Quick Responsive Manufacturing (QRM) solves lead time chaos. Jan Pauwels explains how QRM methodology by Rajan Suri tackles manufacturing complexity.',
    og_title: 'BANI/TUNA Framework — QRM Kills Your Lead Time Problem',
  },
  '/software-partners': {
    title: 'QRM Software Partners | Digital Transformation Tools — Smart QRM Navigator',
    description: 'Proven QRM software partners for digital manufacturing transformation. Tools selected by Jan Pauwels, JP Consulting for Quick Responsive Manufacturing MES integration.',
    og_title: 'QRM Software Partners — Digital Transformation Tools',
  },
  '/implementation-costs': {
    title: 'QRM Implementation Costs | Quick Responsive Manufacturing Investment — Jan Pauwels',
    description: 'Understand Quick Responsive Manufacturing implementation costs. Detailed QRM investment analysis by Jan Pauwels, JP Consulting — from pilot to full-scale rollout.',
    og_title: 'QRM Implementation Costs — Investment Analysis',
  },
  '/financial-lab': {
    title: 'Financial Intelligence Lab | Competitive QRM Analysis — Smart QRM Navigator',
    description: 'Confidential financial intelligence for manufacturers. Benchmark your performance against competitors with QRM-focused analysis by Jan Pauwels, JP Consulting.',
    og_title: 'Financial Intelligence Lab — Competitive QRM Analysis',
  },
  '/contact': {
    title: 'Contact Jan Pauwels | QRM Consulting Inquiry — JP Consulting',
    description: 'Get in touch with Jan Pauwels at JP Consulting for Quick Responsive Manufacturing guidance, strategic consulting, or partnership opportunities.',
    og_title: 'Contact Jan Pauwels — QRM Consulting',
  },
  '/ask-jan': {
    title: 'Ask Jan AI | QRM Questions Answered — Smart QRM Navigator',
    description: 'Ask Jan Pauwels your Quick Responsive Manufacturing questions. AI-powered QRM assistant coming soon from JP Consulting.',
    og_title: 'Ask Jan AI — Your QRM Questions Answered',
  },
};

export const PageSEO = ({ path }) => {
  const seo = SEO_DATA[path] || SEO_DATA['/'];
  const canonical = `${SITE_URL}${path === '/' ? '' : path}`;

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={seo.og_title || seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Smart QRM Navigator" />
    </Helmet>
  );
};

export const HomeJsonLd = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Jan Pauwels",
      "jobTitle": "QRM Consultant & Manufacturing Strategy Expert",
      "worksFor": {
        "@type": "Organization",
        "name": "JP Consulting",
        "url": "https://www.smartqrmnavigator.com"
      },
      "url": "https://www.smartqrmnavigator.com",
      "knowsAbout": ["Quick Responsive Manufacturing", "QRM", "Lean Manufacturing", "POLCA", "Lead Time Reduction", "EBITDA Optimization"],
      "description": "Jan Pauwels is a Quick Responsive Manufacturing (QRM) consultant at JP Consulting with 10 years of experience implementing Rajan Suri's QRM methodology in manufacturing environments."
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Smart QRM Navigator",
      "url": "https://www.smartqrmnavigator.com",
      "description": "AI-powered Quick Responsive Manufacturing strategy platform by Jan Pauwels, JP Consulting.",
      "creator": {
        "@type": "Person",
        "name": "Jan Pauwels"
      }
    })}</script>
  </Helmet>
);
